import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { postLogin, getAccessToken, postLogout, postMasterLogin, postMasterLoginManagement, getMfaToken } from '../../apis/auth'
import { setErrors, resetFormErrors } from '../common/errorsSlice'
import { Login, LoginResponse, ExternalApiType } from '../../types/auth'

interface LoginState {
  current_member_id: number
  company_id: number
  accessToken: string
  isLoading: boolean
  isMasterLogin: boolean
  company_plan: string
  role: ('master' | 'manager' | 'member')[]
  setting_only_master: boolean
  is_team_report_display: boolean
  attendance_alert: boolean
  password_reset: boolean
  slack_connect: boolean
  summary_view: boolean
  hide_analysis: boolean
  hide_support_chat: boolean
  external_api: ExternalApiType
  change_contract: boolean
  editable_weekly_track_time: boolean
  display_create_manual_task: boolean
  enable_project_member_setting: boolean
  function_enabled_state: {
    ai_work_assessment: boolean
    organizational_analysis: boolean
    risk_assessment: boolean
    self_management: boolean
    task_management: boolean
    task_management_approval: boolean
  }
  permissions: {
    task_cost_view: boolean
    task_member_cost_setting: boolean
    task_project_cost_setting: boolean
    task_report_view: boolean
    task_project_setting: boolean
    task_project_member_setting: boolean
    task_approver: boolean
    task_attendance_export: boolean
    task_category_report_view: boolean
    task_overwrite_break_time: boolean
    high_volume: boolean
  }
}

const initialState: Partial<LoginState> = {
  isLoading: true,
  isMasterLogin: false,
}

const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    createLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.current_member_id = action.payload.member?.id
      state.company_id = action.payload.member?.company_id
      state.isLoading = false
      state.isMasterLogin = false
      state.accessToken = action.payload.token
      state.company_plan = action.payload.member?.company_plan
      state.role = action.payload.member?.role
      state.setting_only_master = action.payload.member?.setting_only_master
      state.is_team_report_display = action.payload.member?.is_team_report_display
      state.attendance_alert = action.payload.member?.attendance_alert
      state.password_reset = action.payload.member?.password_reset
      state.slack_connect = action.payload.member?.slack_connect
      state.summary_view = action.payload.member?.summary_view
      state.hide_analysis = action.payload.member.hide_analysis
      state.hide_support_chat = action.payload.member.hide_support_chat
      state.function_enabled_state = action.payload.member.function_enabled_state
      state.permissions = action.payload.member.permissions
      state.external_api = action.payload.member.external_api
      state.editable_weekly_track_time = action.payload.member.editable_weekly_track_time
      state.change_contract = action.payload.member.change_contract
      state.display_create_manual_task = action.payload.member.display_create_manual_task
      state.enable_project_member_setting = action.payload.member.enable_project_member_setting
      return state
    },
    createMasterLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.current_member_id = action.payload.member?.id
      state.company_id = action.payload.member?.company_id
      state.isLoading = false
      state.isMasterLogin = true
      state.accessToken = action.payload.token
      return state
    },
    createLogoutSuccess: (state) => {
      state.current_member_id = undefined
      state.company_id = undefined
      state.isLoading = false
      if (state.isMasterLogin === false) {
        state.accessToken = undefined
      }
      state.company_plan = undefined
      state.role = []
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
    resetLoginState: (state) => {
      // eslint-disable-next-line no-param-reassign
      state = initialState
      return state
    },
  },
})

export const {
  requestStart,
  createLoginSuccess,
  createMasterLoginSuccess,
  createLogoutSuccess,
  requestFailure,
  resetLoginState,
} = loginSlice.actions

export const loginReducer = loginSlice.reducer

export const createLogin = (
  login: Login,
  callback: (data: LoginResponse) => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postLogin(login)
    .then((result) => {
      if (result.token.length > 0) {
        dispatch(createLoginSuccess(result))
      }
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      errorCallback()
      if (error.needResetToken) {
        dispatch(setErrors({
          status: error.status,
          needResetToken: false,
          errors: [{
            message: ['ログインに失敗しました。'],
            column_name: 'form',
          }],
        }))
      } else {
        dispatch(setErrors(error))
      }
    })
}

export const fetchMfaToken = (
  login: Login,
  callback: (data: LoginResponse) => void,
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getMfaToken(login)
    .then((result) => {
      dispatch(createLoginSuccess(result))
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const fetchAccessToken = (
  callback: (data: LoginResponse) => void,
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getAccessToken()
    .then((result) => {
      dispatch(createLoginSuccess(result))
      dispatch(resetFormErrors())
      callback(result)
    })
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const sendLogout = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  postLogout()
    .then(() => {
      dispatch(createLogoutSuccess())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const masterLogin = (
  login: Login,
  callback: (data: Login) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postMasterLogin(login)
    .then((result) => {
      dispatch(createMasterLoginSuccess(result))
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      if (error.needResetToken) {
        dispatch(setErrors({
          status: error.status,
          needResetToken: false,
          errors: [{
            message: ['ログインに失敗しました。'],
            column_name: 'form',
          }],
        }))
      } else {
        dispatch(setErrors(error))
      }
    })
}

export const masterLoginManagement = (
  param: { member_id: number },
  callback: (data: Login) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postMasterLoginManagement(param)
    .then((result) => {
      dispatch(createMasterLoginSuccess(result))
      callback(result)
    })
    .catch(() => dispatch(requestFailure()))
}
